/**
 * Hook to get current user's staff record from staff_master
 * Used for access control checks
 */

import { useBootContext } from '@/hooks/useBootContext';

export type { CurrentUserStaff } from '@/services/bootContextService';

/**
 * A selector over the boot query (useBootContext — one app_boot_context()
 * round trip; docs/plans/2026-10-15-lms-load-speed.md §4 Phase 2). The
 * staff row resolves auth.uid() → staff_emails ∪ staff_master.email
 * (get_my_staff), its role comes from the staff_roles registry: super_admin
 * collapses to 'admin', an ABSENT row is regular 'staff' (by design —
 * teachers have none), permission flags default false.
 *
 * A query error means identity resolution FAILED — a retryable error state
 * (isError), distinct from a confirmed empty result (currentUserStaff ===
 * null, meaning this user genuinely has no staff record). Errors are never
 * swallowed into "not linked".
 */
export function useCurrentUserStaff() {
  const { query, user, authLoading } = useBootContext();

  return {
    // undefined while resolving or on error; null when signed in but not linked.
    currentUserStaff: query.data?.staff,
    // Loading until auth has resolved AND the boot query settled — a
    // disabled query is "pending", not "done" (role-gated pages must never
    // see "loaded, no role" on a cold page load).
    isLoading: authLoading || (!!user && query.isPending),
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    userEmail: user?.email ?? null,
  };
}
